/**
 * The ten dimensions the threat-model scorecard scores, with the ids and display names the backend uses.
 *
 * **Why this exists in `src/` rather than only in the mocks.** The scorecard payload carries
 * `displayName` per dimension, so at runtime the names come from the response and this list is not
 * consulted. But two places need the vocabulary with no response in hand: the "About risk" reference
 * drawer, which explains the rubric before any repository is chosen, and `playwright/models.ts`, whose
 * fixtures have to name real dimensions. Those were separate hand-maintained lists on different id
 * vocabularies, and they had already drifted — the reference drawer said "Defense-in-Depth Coverage"
 * where the scorecard says "Defense in Depth", and keyed it `defense-in-depth` against the scorecard's
 * `defense-in-depth-coverage`. A reader who opened the reference and then a repository's drawer was
 * given two names for one dimension.
 *
 * So: one list, in `utils/` where a route and the mocks can both reach it (`architecture.test.ts` forbids
 * the reverse — `utils/` may only import `utils/`). A rubric change now shows up as a diff here rather
 * than as a silent disagreement in the UI.
 *
 * The **glosses live with the reference drawer**, not here: they are ours, written for a reader, and have
 * no counterpart in the payload. See `RISK_DIMENSIONS` in `routes/repositories-page/risk-dimensions.ts`.
 *
 * Order is the scorecard's own, which the drawer follows as presentation order — not rank. Every
 * dimension carries equal weight in `meanScore`.
 */
export const SCORECARD_DIMENSIONS = [
  { dimension: 'exposure-adjusted-attack-surface', displayName: 'Exposure-Adjusted Attack Surface' },
  { dimension: 'defense-in-depth-coverage', displayName: 'Defense in Depth' },
  { dimension: 'blast-radius-containment', displayName: 'Blast Radius Containment' },
  { dimension: 'authentication-posture', displayName: 'Authentication Posture' },
  { dimension: 'authorization-posture', displayName: 'Authorization Posture' },
  { dimension: 'input-handling-hygiene', displayName: 'Input Handling Hygiene' },
  { dimension: 'secrets-and-credentials-handling', displayName: 'Secrets & Credentials Handling' },
  { dimension: 'dependency-risk-posture', displayName: 'Dependency Risk Posture' },
  { dimension: 'transport-and-network-security', displayName: 'Transport & Network Security' },
  { dimension: 'observability-and-detection', displayName: 'Observability & Detection' },
] as const

/** A scorecard dimension's stable id, e.g. `authorization-posture`. */
export type ScorecardDimensionId = (typeof SCORECARD_DIMENSIONS)[number]['dimension']

/** How many dimensions the scorecard scores. Derived, so prose and fixtures cannot claim a stale count. */
export const SCORECARD_DIMENSION_COUNT = SCORECARD_DIMENSIONS.length
